import { makeStyles, shorthands, tokens } from '@fluentui/react-components';

// Design guide (light / dark / high contrast): only Fluent tokens, radius 4px (borderRadiusMedium),
// base 14px, visible focus, state never conveyed by color alone (icon + text).
// The drawer itself (size "full", header, close, Esc) is GuideDrawer.
export const useFilePreviewStyles = makeStyles({
  // Inside the drawer title (h2 of the guide): one line, ellipsis.
  fileName: {
    display: 'block',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalS,
    fontVariantNumeric: 'tabular-nums',
  },
  location: {
    display: 'block',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Drawer body as a flex column so the viewer fills the drawer height (see GuideDrawer bodyStack)
  drawerBody: {
    display: 'flex',
    flexDirection: 'column',
  },
  body: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    minHeight: 0,
    boxSizing: 'border-box',
  },
  frame: {
    flexGrow: 1,
    width: '100%',
    minHeight: '320px',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  imageContainer: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '320px',
    overflow: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  imageContainerZoomed: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  imageFit: {
    maxWidth: '100%',
    maxHeight: '75vh',
    objectFit: 'contain',
    cursor: 'zoom-in',
  },
  imageZoomed: {
    maxWidth: 'none',
    maxHeight: 'none',
    cursor: 'zoom-out',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    columnGap: tokens.spacingHorizontalS,
  },
  media: {
    width: '100%',
    maxHeight: '75vh',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  text: {
    flexGrow: 1,
    ...shorthands.margin(0),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    overflowY: 'auto',
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  tableContainer: {
    flexGrow: 1,
    overflow: 'auto',
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  headerCell: {
    textTransform: 'uppercase',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground2,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
  },
  cell: {
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: tokens.spacingVerticalS,
    textAlign: 'center',
    minHeight: '240px',
    ...shorthands.border(tokens.strokeWidthThin, 'dashed', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalL),
  },
  emptyIcon: {
    fontSize: '32px',
    color: tokens.colorNeutralForeground2,
  },
  emptyTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptyText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  skeletonBlock: {
    height: '50vh',
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
});
